import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type {
  AssignPlanAssetsRequestDto,
  PreviewAssignmentConflictsRequestDto,
} from '@evimo/client/models';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  PreventiveMaintenanceService,
  PLAN_DETAIL_TABLE_PAGE_SIZE,
  WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
  buildWizardAssignableAssetsQueryKey,
  type PlanDetailTablePagination,
  type WizardAssignableAssetFilters,
  type WizardAssignableAssetsPagination,
} from '~/app/services/preventive-maintenance/preventive-maintenance.service';

export type { WizardAssignableAssetFilters, WizardAssignableAssetsPagination };

export function usePlanAssets(
  planId: string | undefined,
  pagination: PlanDetailTablePagination = {
    page: 1,
    limit: PLAN_DETAIL_TABLE_PAGE_SIZE,
  },
) {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const scopeKey = isSuperAdmin ? 'all-orgs' : organizationId;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'preventive-maintenance',
      'plan-assets',
      planId,
      scopeKey,
      pagination.page,
      pagination.limit,
    ],
    queryFn: async () =>
      PreventiveMaintenanceService.listPlanAssets(planId!, pagination),
    enabled: (isSuperAdmin || !!organizationId) && !!planId,
    placeholderData: keepPreviousData,
  });

  const assignMutation = useMutation({
    mutationFn: (dto: AssignPlanAssetsRequestDto) =>
      PreventiveMaintenanceService.assignPlanAssets(planId!, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'plan-assets', planId],
      });
    },
  });

  return {
    planAssets: query.data?.data ?? [],
    meta: query.data?.meta ?? {
      total: 0,
      page: pagination.page,
      limit: pagination.limit,
      totalPages: 0,
    },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    assignPlanAssets: assignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
  };
}

/** Wizard Step 3 — paginated assignable assets without a persisted planId. */
export function useWizardAssignableAssets(
  filters: WizardAssignableAssetFilters = {},
  pagination: WizardAssignableAssetsPagination = {
    page: 1,
    limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
  },
  options: { enabled?: boolean } = {},
) {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const enabled = options.enabled ?? true;

  // Copy arrays so queryKey / queryFn never share mutable Select state refs.
  const organizationIds = filters.organizationIds
    ? [...filters.organizationIds]
    : [];
  const assetTypeIds = filters.assetTypeIds ? [...filters.assetTypeIds] : [];
  const assetModelIds = filters.assetModelIds ? [...filters.assetModelIds] : [];
  const landlordOrganizationIds = filters.landlordOrganizationIds
    ? [...filters.landlordOrganizationIds]
    : [];
  const siteIds = filters.siteIds ? [...filters.siteIds] : [];

  const requestFilters: WizardAssignableAssetFilters = {
    ...(organizationIds.length > 0 ? { organizationIds } : {}),
    ...(assetTypeIds.length > 0 ? { assetTypeIds } : {}),
    ...(assetModelIds.length > 0 ? { assetModelIds } : {}),
    ...(landlordOrganizationIds.length > 0 ? { landlordOrganizationIds } : {}),
    ...(siteIds.length > 0 ? { siteIds } : {}),
  };

  const requestPagination: WizardAssignableAssetsPagination = {
    page: pagination.page,
    limit: pagination.limit,
  };

  const organizationScopeKey = isSuperAdmin
    ? serializeScopeKey(organizationIds)
    : organizationId;

  const queryKey = buildWizardAssignableAssetsQueryKey(
    organizationScopeKey,
    requestFilters,
    requestPagination,
  );

  const query = useQuery({
    queryKey,
    queryFn: async () =>
      PreventiveMaintenanceService.findWizardAssignableAssets(
        requestFilters,
        requestPagination,
      ),
    // Org users need a tenant id; SUPER_ADMIN may list across orgs (empty = all).
    enabled: enabled && (isSuperAdmin || !!organizationId),
  });

  return {
    assignableAssets: query.data?.data ?? [],
    meta: query.data?.meta ?? {
      total: 0,
      page: requestPagination.page,
      limit: requestPagination.limit,
      totalPages: 0,
    },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

function serializeScopeKey(ids: string[]): string {
  if (ids.length === 0) return 'all-orgs';
  return [...ids].sort().join(',');
}

export function usePreviewConflicts(planId: string | undefined) {
  const previewMutation = useMutation({
    mutationFn: async (dto: PreviewAssignmentConflictsRequestDto) => {
      const res = await PreventiveMaintenanceService.previewConflicts(
        planId!,
        dto,
      );
      return res.data;
    },
  });

  return {
    previewConflicts: previewMutation.mutateAsync,
    conflicts: previewMutation.data ?? [],
    isPreviewing: previewMutation.isPending,
    error: previewMutation.error as Error | null,
  };
}

/** Wizard Step 3 — conflict check for selected assets (no draft plan). */
export function useWizardCheckConflicts() {
  const checkMutation = useMutation({
    mutationFn: async (dto: {
      assetIds: string[];
      /** YYYY-MM-DD — converted for OpenAPI client Date typing */
      startDate: string;
    }) => {
      const res = await PreventiveMaintenanceService.checkWizardConflicts({
        assetIds: dto.assetIds,
        startDate: new Date(`${dto.startDate}T00:00:00.000Z`),
      });
      return res.data;
    },
  });

  return {
    checkWizardConflicts: checkMutation.mutateAsync,
    conflicts: checkMutation.data ?? [],
    isChecking: checkMutation.isPending,
    error: checkMutation.error as Error | null,
  };
}
